import React from 'react';
import { I18NContext } from '../../I18NContext';
import { Item, Order, PatientDrugRegimenType } from '../component.d';
import { getPatientDrugRegimenType } from '../../common/DrugUtils';
import InvoiceCharges from './InvoiceCharges';
import InvoiceAttributeRow from './InvoiceAttributeRow';
import { formatMonDDYYY } from '../common/FormatUtils';
import ErrorBoundary from '../../ErrorBoundary';
import { getLocale } from '../../Utils';
import RebillOrderType from '../../common/Types';


interface InvoiceItemSummaryTagProps {
    item: Item;
    order: Order;
}

class InvoiceItemSummaryTag extends React.Component<InvoiceItemSummaryTagProps, {}> {
    render() {
        const { item, order } = this.props;
        const isRxItem = item && getPatientDrugRegimenType(item.patientDrugRegimenType) !== PatientDrugRegimenType.NON_PRESCRIPTION;

        return (
            <pui-stack>
                <pui-heading input={item.rx?.headline} />
                <pui-text input={item.rx?.subline} />
                {item.hbaEligibility.hbaEligible === "Eligible" && <pui-text input={order.charges.hbaEligibleTotalLabel} />}
                <table style={{ width: '100%' }}>
                    <tbody>
                        {[
                            isRxItem && <InvoiceAttributeRow key="ndc" label={order.invoicePageLabels.ndcId} value={item.rx?.ndc11} />,
                            <InvoiceAttributeRow key="quantity" label={order.invoicePageLabels.itemQuantity} value={item.rx?.quantity?.number} />,
                            <InvoiceAttributeRow key="price" label={order.invoicePageLabels.unitPrice} value={item.charges.unitPrice} />,
                            isRxItem && <InvoiceAttributeRow key="patient" label={order.invoicePageLabels.patient} value={item.patient?.name} />,
                            isRxItem && <InvoiceAttributeRow key="prescription" label={order.invoicePageLabels.prescriptionId} value={item.rx?.prescriptionNumber} />,
                            isRxItem && <InvoiceAttributeRow key="prescriber" label={order.invoicePageLabels.prescriber} value={item.rx?.prescriber} />,
                            // Removed filledOn: was showing fill creation date (not actual dispense date) after
                            // PAPI migration. Order Date and Shipping Date are sufficient. See P379385476
                            // Insurance/other card information should be omitted for Arrow.
                            !(item.insurances && item.insurances.some(insurance => insurance.cardKind.type === "Arrow")) &&
                            <InvoiceAttributeRow key="insurance" label={order.invoicePageLabels.insurance} values={item.insurances?.map(ins => ins.tailLabel!)} />,
                            item.soldBy?.address?.lines?.length &&
                            <ErrorBoundary key="soldBy">
                                <InvoiceAttributeRow label={order.invoicePageLabels.soldBy} values={item.soldBy.address.lines} />
                            </ErrorBoundary>,
                            isRxItem && <InvoiceAttributeRow key="nabp" label={order.invoicePageLabels.nabpNpi} value={item.rx?.nabp} />,
                            this._generateShipSpeedInformation(),
                            <InvoiceCharges key="charges" {...item.charges} />
                        ].filter(Boolean)}
                    </tbody>
                </table>
            </pui-stack>
        )
    }


    _generateShipSpeedInformation() {
        const { order } = this.props;
        const isRebillOrder = order.reverseRebillOrderDetails?.rebillOrderType === RebillOrderType.REBILL_ORDER;
        if (isRebillOrder) {
            return null;
        }

        const shippingDateValue = order.isPickUpOrder
            ? order.invoicePageLabels.notApplicable
            : formatMonDDYYY(this.props.item.shipmentDate?.value, getLocale());

        return <InvoiceAttributeRow key="shipping" label={this.props.order.invoicePageLabels.shippingDate} value={shippingDateValue} />;
    }
}

InvoiceItemSummaryTag.contextType = I18NContext;

export default InvoiceItemSummaryTag;
